import { LEADS, LOOKUP, SHAREABLE_LINKS, STATUSES, USERS } from '../../api/endpoints';
import { buildLeadDetailParams } from '../../components/Leads/utils';
import { baseApi, listTags } from './baseApi';

const multipart =
  typeof FormData !== 'undefined'
    ? {} // Content-Type omitted — axios sets multipart boundary
    : {};

export const leadsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLeads: build.query({
      query: (params = {}) => ({ url: LEADS.LIST, method: 'GET', params }),
      providesTags: (result) => listTags('Lead', result),
    }),
    getLeadById: build.query({
      query: (id) => ({
        url: LEADS.BY_ID(id),
        method: 'GET',
        params: buildLeadDetailParams(),
      }),
      providesTags: (_r, _e, id) => [{ type: 'Lead', id }],
    }),
    importLeads: build.mutation({
      query: (formData) => ({
        url: LEADS.IMPORT,
        method: 'POST',
        data: formData,
        ...multipart,
      }),
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    createLead: build.mutation({
      query: (payload) => {
        const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
        return {
          url: LEADS.LIST,
          method: 'POST',
          data: payload,
          ...(isFormData ? multipart : {}),
        };
      },
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    updateLead: build.mutation({
      query: ({ id, data: updateData }) => {
        const isFormData =
          typeof FormData !== 'undefined' && updateData instanceof FormData;
        return {
          url: LEADS.BY_ID(id),
          method: 'PUT',
          data: updateData,
          ...(isFormData ? multipart : {}),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Lead', id },
        { type: 'Lead', id: 'LIST' },
      ],
    }),
    deleteLead: build.mutation({
      query: (leadId) => ({ url: LEADS.BY_ID(leadId), method: 'DELETE' }),
      invalidatesTags: (_r, _e, leadId) => [
        { type: 'Lead', id: leadId },
        { type: 'Lead', id: 'LIST' },
      ],
    }),
    updateLeadStatus: build.mutation({
      query: ({ leadId, payload }) => ({
        url: LEADS.UPDATE_STATUS(leadId),
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (_r, _e, { leadId }) => [
        { type: 'Lead', id: leadId },
        { type: 'Lead', id: 'LIST' },
      ],
    }),
    assignLeadToUser: build.mutation({
      query: ({ leadId, userIds }) => ({
        url: LEADS.ASSIGN_USER(leadId),
        method: 'POST',
        data: { users: userIds },
      }),
      invalidatesTags: (_r, _e, { leadId }) => [
        { type: 'Lead', id: leadId },
        { type: 'Lead', id: 'LIST' },
        { type: 'Lead', id: `assigned-${leadId}` },
      ],
    }),
    createShareableLink: build.mutation({
      query: (leadId) => ({
        url: SHAREABLE_LINKS.CREATE,
        method: 'POST',
        data: { lead_id: leadId },
      }),
    }),
    getLeadAssignedUsers: build.query({
      query: (leadId) => ({
        url: LEADS.ASSIGNED_USERS(leadId),
        method: 'GET',
      }),
      providesTags: (_r, _e, leadId) => [{ type: 'Lead', id: `assigned-${leadId}` }],
    }),
    getLeadAttachments: build.query({
      query: (leadId) => ({
        url: LEADS.BY_ID(leadId),
        method: 'GET',
      }),
      providesTags: (_r, _e, leadId) => [{ type: 'Lead', id: `attachments-${leadId}` }],
    }),
    downloadLeadFile: build.mutation({
      query: (path) => ({
        url: LEADS.DOWNLOAD,
        method: 'POST',
        data: { path },
        responseType: 'blob',
      }),
    }),
    createCustomerFromLead: build.mutation({
      query: (customerData) => ({
        url: USERS.LIST,
        method: 'POST',
        data: customerData,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    getLeadsStatus: build.query({
      query: (params = {}) => ({ url: STATUSES.LIST, method: 'GET', params }),
      providesTags: [{ type: 'Status', id: 'LEAD' }],
    }),
    getLeadSources: build.query({
      query: () => ({ url: LOOKUP.SOURCES, method: 'GET' }),
      providesTags: [{ type: 'Lookup', id: 'SOURCES' }],
    }),
    getLeadBrands: build.query({
      query: () => ({ url: LOOKUP.BRANDS, method: 'GET' }),
      providesTags: [{ type: 'Brand', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetLeadsQuery,
  useLazyGetLeadsQuery,
  useGetLeadByIdQuery,
  useLazyGetLeadByIdQuery,
  useImportLeadsMutation,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
  useUpdateLeadStatusMutation,
  useAssignLeadToUserMutation,
  useCreateShareableLinkMutation,
  useGetLeadAssignedUsersQuery,
  useLazyGetLeadAssignedUsersQuery,
  useGetLeadAttachmentsQuery,
  useLazyGetLeadAttachmentsQuery,
  useDownloadLeadFileMutation,
  useCreateCustomerFromLeadMutation,
  useGetLeadsStatusQuery,
  useLazyGetLeadsStatusQuery,
  useGetLeadSourcesQuery,
  useLazyGetLeadSourcesQuery,
  useGetLeadBrandsQuery,
  useLazyGetLeadBrandsQuery,
} = leadsApi;
